import { useCallback } from 'react';
import { Avatar, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { SectionLabel, DetailSections } from '../shared/index.js';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { initialsFromName } from '../../../utils/initials.js';
import { confidencePercent, formatDate } from '../../../utils/detailFormat.js';
import { humanizeReason } from '../../../utils/textCase.js';

/**
 * Gov Change Request detail body for an interim `change_request` event
 * (CT-5092/5095), modeled on the HTL prototype's "Truth Conflict Resolve"
 * card (EVT-012), adapted to the fields the real my-work backend provides:
 *
 *  - CHANGE DETAIL — requester (createdByName), rationale (reason), change type
 *    (operationType). From the list row + preview.
 *  - COMPARISON — CURRENT (truthCurrentText) vs PROPOSED (proposedValueText) as
 *    two cards, with risk/confidence on the proposed side. The prototype's
 *    per-side confidence/verified/owner and IMPACT (affected agents) are NOT on
 *    the wire, so they are intentionally omitted.
 *  - PROPOSE CORRECTION — an inline editor (shown while the footer is in edit
 *    mode) prefilled with the proposed text; submitting files a counter-proposal
 *    via change-request/create-on-change-request (the parent CR stays open —
 *    resolve only supports approve/reject, so there is no "accept with changes").
 *  - APPROVALS — approvalCount / requiredApprovals (stands in for the mock IMPACT).
 *
 * The body writes the correction draft to the shared EventDraft (never the
 * WorkQueue context), so it survives isolated renders; the footer performs the call.
 */

const OPERATION_LABEL_KEY = {
  CREATE_FACT: 'changeRequest.opCreate',
  UPDATE_FACT: 'changeRequest.opEdit',
  DELETE_FACT: 'changeRequest.opDelete',
  MERGE_FACT: 'changeRequest.opMerge',
};

function ChangeRequestBody({ event }) {
  const { t } = useTranslation('work-queues-app');
  const { editing, draft, setDraft } = useEventDraft();
  const handleDraftChange = useCallback((e) => setDraft(e.target.value), [setDraft]);
  const detail = event.detail ?? {};

  const requester = detail.createdByName ?? detail.requestedBy ?? detail.assignedTo ?? null;
  const requestedAt = formatDate(detail.preview?.createdAt ?? detail.requestedOn ?? detail.updatedAt);
  const rationale = detail.requestReason ?? detail.rationale ?? null;
  // Known operations get a translated badge; an unknown value the backend adds
  // later shows its humanized token rather than being mislabelled.
  const operationLabel = detail.operationType
    ? OPERATION_LABEL_KEY[detail.operationType]
      ? t(OPERATION_LABEL_KEY[detail.operationType])
      : humanizeReason(detail.operationType)
    : null;

  const proposed = detail.proposedStatement ?? event.situation ?? null;
  const current = detail.currentStatement ?? null;
  const confidence = confidencePercent(detail.confidence);
  const risk = detail.riskLevel ?? null;

  const approvalCount = detail.approvalCount;
  const requiredApprovals = detail.requiredApprovals;
  const showApprovals =
    approvalCount != null &&
    requiredApprovals != null &&
    Number.isFinite(Number(approvalCount)) &&
    Number.isFinite(Number(requiredApprovals)) &&
    Number(requiredApprovals) > 0;

  const changeDetail =
    requester || rationale || operationLabel ? (
      <div key="change-detail">
        <SectionLabel>{t('changeRequest.changeDetail')}</SectionLabel>
        {requester && (
          <div className="flex items-center gap-2.5">
            <Avatar
              size="md"
              initials={initialsFromName(requester)}
              alt={requester}
              className="border-transparent bg-[#2173FF]/15 text-[10px] font-bold text-content"
            />
            <div>
              <p className="text-[13px] font-semibold leading-tight text-content">{requester}</p>
              {requestedAt && (
                <p className="text-[11px] text-content-faint">{t('changeRequest.requested', { date: requestedAt })}</p>
              )}
            </div>
          </div>
        )}
        {rationale && (
          <p className="mt-2.5 rounded-lg border border-edge bg-surface-muted px-3 py-2 text-xs leading-relaxed text-content-secondary">
            {rationale}
          </p>
        )}
        {operationLabel && (
          <div className="mt-3">
            <p className="text-[11px] uppercase tracking-[0.06em] text-content-faint">{t('changeRequest.changeType')}</p>
            <p className="mt-0.5 text-[13px] font-semibold text-content">{operationLabel}</p>
          </div>
        )}
      </div>
    ) : null;

  const comparison = proposed ? (
    <div key="comparison">
      <SectionLabel>{t('changeRequest.comparison')}</SectionLabel>
      <div className="flex flex-col gap-3 sm:flex-row">
        {current && (
          <div className="flex-1 rounded-lg border border-edge bg-surface-muted p-4">
            <span className="mb-2.5 inline-block rounded bg-sky-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-sky-400">
              {t('changeRequest.currentValue')}
            </span>
            <p className="break-words text-[13px] leading-relaxed text-content-secondary">{current}</p>
          </div>
        )}
        <div className="flex-1 rounded-lg border border-edge bg-surface-muted p-4">
          <span className="mb-2.5 inline-block rounded bg-violet-500/15 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-violet-400">
            {t('changeRequest.proposedValue')}
          </span>
          <p className="break-words text-[13px] leading-relaxed text-content">{proposed}</p>
          {(risk || confidence) && (
            <dl className="mt-3 border-t border-edge pt-2 text-[12px]">
              {risk && (
                <div className="flex justify-between py-1">
                  <dt className="text-content-faint">{t('changeRequest.risk')}</dt>
                  <dd className="font-medium text-amber-500">{risk}</dd>
                </div>
              )}
              {confidence && (
                <div className="flex justify-between py-1">
                  <dt className="text-content-faint">{t('changeRequest.confidence')}</dt>
                  <dd className="font-medium text-content">{confidence}</dd>
                </div>
              )}
            </dl>
          )}
        </div>
      </div>
    </div>
  ) : null;

  const correction = editing ? (
    <div key="correction">
      <SectionLabel>{t('changeRequest.proposeCorrection')}</SectionLabel>
      <TextArea
        rows={4}
        value={draft}
        placeholder={t('changeRequest.correctionPlaceholder')}
        onChange={handleDraftChange}
      />
    </div>
  ) : null;

  const approvals = showApprovals ? (
    <div key="approvals">
      <SectionLabel>{t('changeRequest.approvals')}</SectionLabel>
      <p className="text-[13px] text-content-secondary">
        {t('changeRequest.approvalsReceived', { count: approvalCount, required: requiredApprovals })}
      </p>
    </div>
  ) : null;

  const sections = [changeDetail, comparison, correction, approvals].filter(Boolean);

  if (!sections.length) {
    return <p className="text-sm leading-relaxed text-content-secondary">{event.situation ?? event.description}</p>;
  }

  return <DetailSections sections={sections} />;
}

export default ChangeRequestBody;
